/**
 * TODO in all forms use AutocompleteController instead of ReactHookSelect
 */

import React, { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'
import { FieldErrors } from 'react-hook-form/dist/types/errors'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { OptionType } from '../models'
import whiteCheck from 'assets/icons/white-check.svg'

interface ReactHookSelectType {
  defaultValue?: OptionType | string
  label: string
  name: string
  multiple?: boolean
  options: OptionType[]
  control: any
  rules?: RegisterOptions
  errors: FieldErrors
  /**
   * Если true, то select в качестве своего value отдает и принимает
   * не объект option, а value из option
   */
  useScalarValue?: boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: '20px 0 0',
      '& .MuiSelect-icon': {
        right: 10,
      },
      '& .MuiInputBase-root': {
        marginTop: 0,
      },
      '& .MuiInputBase-input': {
        borderRadius: 30,
        backgroundColor: theme.colors.greySemiLight,
        padding: '10px 16px 11px',

        [theme.breakpoints.up(1024)]: {
          padding: '14px 24px 15px',
        },
      },
      '& .MuiInputLabel-root': {
        transform: 'translate(16px, 34px)',
        maxWidth: 'calc(100% - 16px)',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        color: theme.colors.greySemiDark,

        [theme.breakpoints.up(1024)]: {
          maxWidth: 'calc(100% - 24px)',
          transform: 'translate(24px, 35px)',
        },

        [theme.breakpoints.down(1023)]: {
          fontSize: 12,
        },
      },
      '& .MuiInputLabel-shrink': {
        transform: 'translate(16px, 0)!important',
        fontSize: 12,

        [theme.breakpoints.up(1024)]: {
          transform: 'translate(24px, 0)!important',
        },
      },
      '& .MuiFormHelperText-root': {
        minHeight: 20,
        fontFamily: theme.fonts.inter,
        color: theme.colors.red,
        letterSpacing: 'normal',
        fontSize: 12,
        lineHeight: 1.5,
        maxWidth: '100%',
        paddingLeft: 16,
        marginTop: 0,

        [theme.breakpoints.up(1024)]: {
          paddingLeft: 24,
        },
      },
    },
    menuPaper: {
      boxShadow: `
        0px 40px 80px rgba(173, 173, 173, 0.07),
        0px 20px 33.4221px rgba(173, 173, 173, 0.0503198),
        0px 10px 17.869px rgba(173, 173, 173, 0.0417275),
        0px 4px 10.0172px rgba(173, 173, 173, 0.035),
        0px 2px 5.32008px rgba(173, 173, 173, 0.0282725),
        0px 0px 2.21381px rgba(173, 173, 173, 0.0196802);
      `,
      borderRadius: 8,
      '& .MuiListItem-root': {
        '& .select-checkbox': {
          position: 'relative',
          paddingLeft: 24,

          '& .select-checkbox__icon': {
            position: 'absolute',
            left: 0,
            top: 4,
            display: 'block',
            width: 16,
            height: 16,
            borderRadius: 4,
            border: `1px solid ${theme.colors.black}`,
          },
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: theme.colors.greyLight,
        },
        '&.Mui-selected': {
          '& .select-checkbox__icon': {
            background: `${theme.colors.black} url(${whiteCheck}) no-repeat 40% 60%`,
            backgroundSize: 9,
          },
        },
      },
    },
  })
)

const ReactHookSelect = (props: ReactHookSelectType) => {
  const { name, label, options, multiple, defaultValue, control, rules, errors, useScalarValue = false } = props
  const classes = useStyles()

  const optionsMapByValue = useMemo(() => {
    const ret = {} as { [key: string | number]: OptionType }
    if (useScalarValue) {
      options.forEach(item => {
        ret[item.value] = item
      })
    }
    return ret
  }, [options])
  return (
    <div>
      <Controller
        render={({ field: { onChange, value, name, ref } }) => (
          <TextField
            fullWidth
            InputLabelProps={{
              variant: 'filled',
            }}
            classes={{
              root: classes.root,
            }}
            SelectProps={{
              MenuProps: {
                variant: 'menu',
                classes: {
                  paper: classes.menuPaper,
                },
              },
              multiple: multiple || undefined,
              renderValue: selectedValue => {
                // @ts-ignore
                const selected = useScalarValue ? optionsMapByValue[selectedValue] : selectedValue
                if (multiple) {
                  return (selected as OptionType[]).map(e => e?.label || e).join(', ')
                }
                return (selected as OptionType).label
              },
            }}
            defaultValue={defaultValue}
            value={value || ''}
            onChange={onChange}
            inputRef={ref}
            label={label}
            id={name}
            helperText={<span>{errors?.[name]?.message}</span>}
            select
          >
            {options.map((option: any) => {
              const { value, label } = option
              return (
                <MenuItem key={value} value={useScalarValue ? value : option}>
                  {multiple ? (
                    <div className={'select-checkbox'}>
                      <i className="select-checkbox__icon"></i>
                      {label}
                    </div>
                  ) : (
                    label
                  )}
                </MenuItem>
              )
            })}
          </TextField>
        )}
        defaultValue={defaultValue}
        name={name}
        control={control}
        rules={rules}
      />
    </div>
  )
}

export default ReactHookSelect
